import React, { useEffect } from 'react';
import { Icon } from 'components';
import { theme } from 'lib';

const Modal = ({
  visible,
  closeModal,
  children,
  width,
  wid, // Legacy support
  onOverlayClick,
  header,
  footer,
  hideCloseButton = false,
  noBorderHeader = false,
  noBorderFooter = false,
  footerBackground,

  // Legacy props
  selfContent,
  website,
  modalBg,
  style,
  waiting = false,
  noOutsideClick,
  closeIconFill = theme.GREY_80,
  contentArea2MaxHt,
  ...props
}) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && visible) {
        closeModal && closeModal();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [visible, closeModal]);

  if (!visible) return null;

  const handleOverlayClick = (e) => {
    if (noOutsideClick) return;

    // Check if clicked exactly on the overlay (the generic container)
    // We use a specific ID or class to identify the overlay if e.target === e.currentTarget check isn't sufficient due to wrapping
    if (e.target === e.currentTarget) {
      if (onOverlayClick) {
        onOverlayClick();
      } else {
        closeModal && closeModal();
      }
    }
  };

  // Determine width based on props
  const finalWidth = width || wid || (website ? '700px' : '456px');

  // Logic for selfContent (legacy bypass)
  if (selfContent) {
    return (
      <div
        className="fixed inset-0 z-[1000] flex items-center justify-center animate-fadeIn"
        onClick={handleOverlayClick}
        style={{
          backgroundColor: modalBg || 'rgba(0, 0, 0, 0.7)',
          ...style
        }}
      >
        <div className="relative flex items-center justify-center w-full box-border">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center animate-fadeIn p-5"
      style={{
        backgroundColor: modalBg || 'rgba(0, 0, 0, 0.7)',
        ...style
      }}
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-2xl relative flex flex-col shadow-2xl overflow-hidden animate-slideUp max-w-[95vw] max-h-[90vh]"
        style={{
          width: finalWidth,
          maxHeight: contentArea2MaxHt || '90vh'
        }}
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside modal from closing it
      >
        {/* Header Section */}
        {header ? (
          <div
            className={`shrink-0 px-6 pt-6 pb-4 relative ${
              !noBorderHeader ? 'border-b border-grey-20' : ''
            }`}
          >
            {!hideCloseButton && !waiting && (
              <button
                type="button"
                onClick={closeModal}
                className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-transparent hover:bg-grey-10 active:scale-95 transition-all outline-none"
              >
                <Icon type="close" dimensions={14} col={theme.GREY_80} />
              </button>
            )}
            {header}
          </div>
        ) : (
          /* No Header but Close Button needs to exist */
          !hideCloseButton &&
          !waiting && (
            <button
              type="button"
              onClick={closeModal}
              className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full bg-transparent hover:bg-grey-10 active:scale-95 transition-all z-10 outline-none"
            >
              <Icon type="close" dimensions={14} col={theme.GREY_80} />
            </button>
          )
        )}

        {/* Content Section */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 scrollbar-thin scrollbar-thumb-grey-40 scrollbar-track-grey-10 hover:scrollbar-thumb-grey-60">
          {children}
        </div>

        {/* Footer Section */}
        {footer && (
          <div
            className={`shrink-0 px-6 pt-4 pb-6 ${
              !noBorderFooter ? 'border-t border-grey-20' : ''
            }`}
            style={{ background: footerBackground || 'white' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
